import React, { useEffect, useMemo, useState } from "react";
import { t } from "ttag";

import { templates } from "@reactivated";

import { RichText } from "../../../common/RichText";
import { getOptionValueHash } from "../../../utils/catalogue";
import { sortProductOptions } from "../../../utils/sorting";
import { ShopTileType } from "../ShopCompareTypes";
import { ShopTile } from "../ShopTile";

import styles from "../ShopCompare/index.module.scss";

export const ShopCompareGrid = (
    props: templates.ShopCompareGridTemplate,
): React.JSX.Element | null => {
    const tileset = props.tileset;
    const tiles = tileset?.shop_tiles?.value ?? [];

    const allSizeOptions = useMemo(() => {
        return tiles
            .map((shopTileObject) => {
                const shopTile = shopTileObject.value.shop_tile;
                const sizeOptions = (shopTile as { size_options?: string[] })
                    ?.size_options;
                if (!shopTile || !sizeOptions) {
                    return undefined;
                }
                return Array.isArray(sizeOptions) ? sizeOptions : undefined;
            })
            .filter((arr): arr is string[] => Array.isArray(arr));
    }, [tiles]);

    const availableSizes = useMemo(() => {
        if (allSizeOptions.length === 0) return [] as string[];
        const allSizes = allSizeOptions.reduce((acc, curr) => {
            return [...acc, ...curr];
        }, [] as string[]);
        const commonSizes = [...new Set(allSizes)];
        return sortProductOptions("option_size", commonSizes);
    }, [allSizeOptions]);

    const tileCount = tiles.length;

    const gridClass = useMemo(() => {
        if (tileCount >= 6) return styles.cols6;
        if (tileCount === 5) return styles.cols5;
        if (tileCount === 4) return styles.cols4;
        if (tileCount === 3) return styles.cols3;
        return "";
    }, [tileCount]);

    const defaultSelected = useMemo(() => {
        if (availableSizes.length > 0) {
            return availableSizes[0];
        }

        return "";
    }, [availableSizes]);

    const [selectedSize, setSelectedSize] = useState<string>(defaultSelected);

    useEffect(() => {
        setSelectedSize(defaultSelected);
    }, [defaultSelected]);

    if (!tileset) {
        return null;
    }

    const selectID = "size-selector";
    const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
    };
    const onSelectChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        setSelectedSize(e.target.value);
    };

    return (
        <section className={styles.shopCompareContainer}>
            <div className={styles.headerContainer}>
                <RichText className={styles.header} html={props.header_copy} />
                {tileset.show_size_selector && availableSizes.length > 0 && (
                    <form className={styles.sizeSelector} onSubmit={onSubmit}>
                        <label
                            htmlFor={selectID}
                        >{t`Price by mattress size`}</label>
                        <span className={styles.selectWrapper}>
                            <select
                                id={selectID}
                                aria-label={t`Select mattress size`}
                                className="al-compareresults-selectsize"
                                onChange={onSelectChange}
                                value={selectedSize}
                            >
                                {availableSizes.map((option) => {
                                    return (
                                        <option
                                            key={getOptionValueHash(option)}
                                            value={option}
                                        >
                                            {option}
                                        </option>
                                    );
                                })}
                            </select>
                        </span>
                    </form>
                )}
            </div>
            <div className={`${styles.shopTileContainer} ${gridClass}`}>
                {tiles.map((tile, index) => {
                    const tileId = tile.value.shop_tile?.id || `tile-${index}`;
                    return (
                        <ShopTile
                            key={tileId}
                            value={tile.value as ShopTileType}
                            tilesetId={tileset.id}
                            selectedSize={selectedSize}
                            compactPricing={tileCount >= 4}
                        />
                    );
                })}
            </div>
            {props.footer_copy && props.footer_copy.trim() !== "" && (
                <div className={styles.footer}>
                    <RichText html={props.footer_copy} />
                </div>
            )}
        </section>
    );
};
